/**
 * Review (11-04, Adam: "give me the list of recs and drafts to look through and approve on a little site"). Everything
 * waiting on a manager in one place, made for a pass on the iPad: the agents' recommendations, the report drafts, the
 * Work-tab sort and the client list's status. Every button uses the same endpoint as the page it came from, so each
 * decision is the signed-in person's, logged as theirs.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { Empty, useToast } from '@caios/ui';
import { DeliveryTabs } from '../delivery-nav.tsx';
import { authClient, type Me } from '../auth.ts';
import { call, One, Reject, type Rec } from './recommendations.tsx';
import './recommendations.css';
import './review.css';

type Report = { id: string; account: string; type: string; audience: string; title: string; period_label: string; period_start: string; period_end: string; status: string;
  enrich_status: string | null; enrich_error: string | null; archived_at: string | null; degraded: boolean | null };
type Work = { id: string; account: string; channel: string; title: string; detail: string | null; status: string; occurred_on: string; suggest: 'client' | 'internal' | 'check'; why: string };
type Client = { id: string; name: string; status: string; relationship: string };
type Tab = 'recs' | 'reports' | 'work' | 'clients';

const TYPE: Record<string, string> = { 'monthly-seo': 'SEO', 'monthly-ads': 'Ads', 'monthly-paid-media': 'Paid media', 'weekly-paid-media': 'Weekly paid media' };
const day = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
/** A report covering a whole calendar month: the October report a client expects. */
const fullMonth = (r: Report) => r.period_start.endsWith('-01') && new Date(`${r.period_end}T12:00:00Z`).getUTCDate() === new Date(Date.UTC(+r.period_end.slice(0, 4), +r.period_end.slice(5, 7), 0)).getUTCDate();

export function Review() {
  const { me } = useRouteContext({ from: '/_app' }) as { me: Me };
  const manage = ['owner', 'admin', 'manager'].includes(me.role);
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('recs');
  const recs = useQuery({ queryKey: ['recs'], queryFn: () => call<Rec[]>('GET', '/api/v1/delivery/recommendations') });
  const reports = useQuery({ queryKey: ['review-reports'], queryFn: () => call<Report[]>('GET', '/api/v1/delivery/reports') });
  const work = useQuery({ queryKey: ['review-work'], enabled: manage, queryFn: () => call<Work[]>('GET', '/api/v1/delivery/work-events/review') });
  const clients = useQuery({ queryKey: ['review-clients'], queryFn: () => call<Client[]>('GET', '/api/v1/clients') });
  // Each partner's clients live in that partner's workspace: the page reviews one workspace at a time.
  const orgs = useQuery({ queryKey: ['my-orgs'], queryFn: async () => (await authClient.organization.list()).data ?? [] });
  const [open, setOpen] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string[] | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  const act = async (ids: string[], fn: () => Promise<unknown>, done: string, keys: string[]) => {
    setBusy((b) => new Set([...b, ...ids]));
    try { await fn(); toast(<>{done}</>); } catch (e) { toast(<>{(e as Error).message}</>); }
    setBusy((b) => { const n = new Set(b); ids.forEach((i) => n.delete(i)); return n; });
    for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
  };
  const decide = (ids: string[], action: 'approve' | 'reject' | 'go' | 'reopen', reason?: string) => act(ids,
    () => call('POST', '/api/v1/delivery/recommendations/decide', { ids, action, ...(reason ? { reason } : {}) }),
    action === 'reject' ? `Rejected ${ids.length}.` : action === 'go' ? 'Go: the agent picks it up next.' : action === 'reopen' ? 'Reopened: back with the agent, and off the client’s Work tab.' : `Approved ${ids.length}: on the client’s task list.`, ['recs'])
    .then(() => { setOpen(null); setRejecting(null); });
  const report = (r: Report, body: { status?: 'published'; archived?: boolean }) => act([r.id], () => call('PATCH', `/api/v1/delivery/reports/${r.id}`, body),
    body.status ? `Published: ${r.account} ${TYPE[r.type] ?? r.type}, ${r.period_label}.` : `Hidden: ${r.account} ${r.period_label}.`, ['review-reports']);
  const sort = (items: Work[], visibility: 'client' | 'internal') => act(items.map((w) => w.id),
    () => Promise.all(items.map((w) => call('POST', `/api/v1/delivery/work-events/${w.id}/decide`, { status: 'confirmed', visibility }))),
    visibility === 'client' ? `${items.length} shown to the client.` : `${items.length} kept internal.`, ['review-work']);

  const proposed = (recs.data ?? []).filter((r) => r.status === 'proposed');
  const waiting = (recs.data ?? []).filter((r) => r.status === 'approved');
  const drafts = (reports.data ?? []).filter((r) => r.audience === 'client' && r.status === 'draft' && !r.archived_at);
  const october = drafts.filter((r) => r.period_start >= '2026-10-01' && fullMonth(r));
  const older = drafts.filter((r) => !october.includes(r)).sort((a, b) => b.period_end.localeCompare(a.period_end));
  const items = work.data ?? [];
  const roster = (clients.data ?? []).filter((c) => c.relationship === 'client');
  const opened = (recs.data ?? []).find((r) => r.id === open);
  const count: Record<Tab, number> = { recs: proposed.length + waiting.length, reports: drafts.length, work: items.length, clients: roster.length };

  return (
    <main className="cx-stack rc rv" style={{ minWidth: 0 }}>
      <header className="cx-page-head cx-rise" style={{ marginBottom: 0 }}>
        <div><div className="cx-eyebrow"><b>Review</b> — everything waiting on you, in one pass</div>
          <h1 className="cx-display">{count.recs + count.reports} to decide{items.length ? `, ${items.length} work items to sort` : ''}</h1></div>
      </header>
      <DeliveryTabs />
      {(orgs.data ?? []).length > 1 && <div className="rv-orgs" data-testid="rv-orgs"><span>Workspace</span>
        {(orgs.data ?? []).map((o) => <button key={o.id} type="button" className={`cx-btn cx-btn--sm${o.id === me.orgId ? ' cx-btn--primary' : ''}`} aria-pressed={o.id === me.orgId}
          onClick={() => { if (o.id !== me.orgId) void authClient.organization.setActive({ organizationId: o.id }).then(() => window.location.assign('/delivery/review')); }}>{o.name}</button>)}
        <small>Review each one: its clients’ items only show in its own workspace.</small></div>}
      <div className="cx-seg rv-tabs" role="tablist" aria-label="What to review">
        {([['recs', 'Recommendations'], ['reports', 'Report drafts'], ['work', 'Work tab'], ['clients', 'Clients']] as const).map(([k, l]) =>
          <button key={k} type="button" role="tab" aria-pressed={tab === k} onClick={() => setTab(k)} data-testid={`rv-tab-${k}`}>{l} <span className="rv-n">{count[k]}</span></button>)}
      </div>

      {tab === 'recs' && <>
        {recs.isSuccess && !proposed.length && !waiting.length && <Empty icon="harbor" title="Nothing to approve">The agents file what they find each Monday.</Empty>}
        {waiting.length > 0 && <section className="rc-batch rc-batch--go"><div className="rc-batch-head"><b>Approved, waiting for your go</b><span>Big changes: the live site or the budget</span></div>
          {waiting.map((r) => <RecRow key={r.id} r={r} onOpen={() => setOpen(r.id)} busy={busy.has(r.id)}
            actions={manage && <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" onClick={() => void decide([r.id], 'go')}>Go</button>} />)}</section>}
        {[...new Set(proposed.map((r) => r.account))].map((c) => {
          const xs = proposed.filter((r) => r.account === c);
          return (
            <section key={c} className="rc-batch" data-testid="rv-rec-batch">
              <div className="rc-batch-head"><b>{c}</b><span>{xs.length} item{xs.length === 1 ? '' : 's'}</span><span className="rc-spacer" />
                {manage && xs.length > 1 && <button type="button" className="cx-btn cx-btn--sm" onClick={() => void decide(xs.map((r) => r.id), 'approve')}>Approve all {xs.length}</button>}</div>
              {xs.map((r) => <RecRow key={r.id} r={r} onOpen={() => setOpen(r.id)} busy={busy.has(r.id)} actions={manage && <>
                <button type="button" className="cx-btn cx-btn--sm" onClick={() => setRejecting([r.id])}>Reject</button>
                <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" onClick={() => void decide([r.id], 'approve')} data-testid="rv-approve">Approve</button></>} />)}
            </section>
          );
        })}
      </>}

      {tab === 'reports' && <>
        {reports.isSuccess && !drafts.length && <Empty icon="harbor" title="No drafts">Every client report is published.</Empty>}
        {october.length > 0 && <ReportGroup title="October, whole month" note="The reports clients expect for October" rows={october} busy={busy} manage={manage} onAct={report} />}
        {older.length > 0 && <ReportGroup title="Earlier and part-month drafts" note="Mid-month and weekly drafts the monthly ones mostly replace: publish the ones worth sending, hide the rest" rows={older} busy={busy} manage={manage} onAct={report} />}
      </>}

      {tab === 'work' && <>
        <p className="rv-note">Past work for each client, still internal. Showing an item puts it on the client’s Work tab and in their next report.
          My suggestion is ticked; nothing changes until you press a button.</p>
        {work.isSuccess && !items.length && <Empty icon="harbor" title="All sorted">Every item is either on the client’s Work tab or kept internal.</Empty>}
        {(['client', 'check', 'internal'] as const).map((g) => {
          const xs = items.filter((w) => w.suggest === g);
          if (!xs.length) return null;
          return (
            <section key={g} className="rc-batch" data-testid={`rv-work-${g}`}>
              <div className="rc-batch-head"><b>{g === 'client' ? 'Suggest: show the client' : g === 'check' ? 'Your call' : 'Suggest: keep internal'}</b><span>{xs.length}</span><span className="rc-spacer" />
                {manage && g !== 'check' && <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={xs.some((w) => busy.has(w.id))} onClick={() => void sort(xs, g)}>
                  {g === 'client' ? `Show all ${xs.length}` : `Keep all ${xs.length} internal`}</button>}</div>
              {xs.map((w) => (
                <div key={w.id} className={`rv-row${busy.has(w.id) ? ' rv-busy' : ''}`}>
                  <div className="rv-main"><b>{w.title}</b><small>{w.account} · {day(w.occurred_on)}{w.detail ? ` · ${w.detail.slice(0, 220)}` : ''}</small><small className="rv-why">{w.why}</small></div>
                  {manage && <div className="rv-acts"><button type="button" className="cx-btn cx-btn--sm" onClick={() => void sort([w], 'internal')}>Internal</button>
                    <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" onClick={() => void sort([w], 'client')}>Show</button></div>}
                </div>
              ))}
            </section>
          );
        })}
      </>}

      {tab === 'clients' && <section className="rc-batch">
        <div className="rc-batch-head"><b>Who is a client</b><span>A prospect leaves Clients, the portal and billing until they sign</span></div>
        {roster.map((c) => (
          <div key={c.id} className={`rv-row${busy.has(c.id) ? ' rv-busy' : ''}`}>
            <div className="rv-main"><b>{c.name}</b></div>
            <select className="cx-input rv-select" value={c.status} disabled={!manage} aria-label={`${c.name} status`}
              onChange={(e) => void act([c.id], () => call('PATCH', `/api/v1/clients/${c.id}`, { status: e.target.value }), `${c.name}: ${e.target.value}.`, ['review-clients', 'clients'])}>
              {['prospect', 'onboarding', 'active', 'paused', 'churned'].map((s) => <option key={s} value={s}>{s === 'churned' ? 'former' : s}</option>)}
            </select>
          </div>
        ))}
      </section>}

      {opened && <One r={opened} manage={manage} onClose={() => setOpen(null)} onDecide={(ids, a) => decide(ids, a)} onReject={() => setRejecting([opened.id])} onEdited={() => void qc.invalidateQueries({ queryKey: ['recs'] })} />}
      {rejecting && <Reject n={rejecting.length} onCancel={() => setRejecting(null)} onReject={(reason) => void decide(rejecting, 'reject', reason)} />}
    </main>
  );
}

function RecRow({ r, onOpen, actions, busy }: { r: Rec; onOpen: () => void; actions: React.ReactNode; busy: boolean }) {
  return (
    <div className={`rv-row${busy ? ' rv-busy' : ''}`} data-testid="rv-rec">
      <button type="button" className="rv-main rv-open" onClick={onOpen}><b>{r.title}</b>
        <small>{r.discipline === 'ads' ? 'Ads' : 'SEO'} · {r.size === 'big' ? 'needs your go after approval' : 'on-page'} · {r.discipline === 'ads' ? 'Ads' : 'SEO'} agent takes it{r.doer === 'person' ? ', then a person' : ''}</small>
        <small className="rv-why">{r.why}</small></button>
      <div className="rv-acts">{actions}</div>
    </div>
  );
}

function ReportGroup({ title, note, rows, busy, manage, onAct }: { title: string; note: string; rows: Report[]; busy: Set<string>; manage: boolean; onAct: (r: Report, b: { status?: 'published'; archived?: boolean }) => Promise<void> }) {
  return (
    <section className="rc-batch" data-testid="rv-reports">
      <div className="rc-batch-head"><b>{title}</b><span>{rows.length} · {note}</span></div>
      {rows.map((r) => {
        const notReady = r.enrich_status === 'pending' || r.enrich_status === 'claimed';
        return (
          <div key={r.id} className={`rv-row${busy.has(r.id) ? ' rv-busy' : ''}`} data-testid="rv-report">
            <div className="rv-main"><b>{r.account} · {TYPE[r.type] ?? r.type}</b>
              <small>{r.period_label}{r.degraded ? ' · some data was missing' : ''}{notReady ? ' · the summary is still being written' : ''}{r.enrich_error ? ` · ${r.enrich_error}` : ''}</small></div>
            <div className="rv-acts">
              <a className="cx-btn cx-btn--sm" href={`/api/v1/delivery/reports/${r.id}/view`} target="_blank" rel="noopener">Read</a>
              {manage && <><button type="button" className="cx-btn cx-btn--sm" onClick={() => void onAct(r, { archived: true })}>Hide</button>
                <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={notReady} onClick={() => void onAct(r, { status: 'published' })} data-testid="rv-publish">Publish</button></>}
            </div>
          </div>
        );
      })}
    </section>
  );
}
